import { API, graphqlOperation, Storage } from "aws-amplify";
import { getInventorieValidate } from "../../../graphql/inventory/custom-queries";
import { deleteInventoryItem, deletePhoto } from "../../../graphql/mutations";

import { InputAdornment, Tooltip, Typography } from "@material-ui/core";

import {
  InventoryStatus,
  LocationType,
  ShipmentLineItemStatus,
  VALIDATION_MSG,
} from "../../../utils/constants";
import {
  isBulkLocation,
  isManagedLocation,
  promiseSetCapacityUsed,
} from "../../../utils/locationUtils";
import {
  getUnitConversions,
  getUnitName,
  getYardCommodity,
} from "../../../utils/loadTypeUtils";
import { trimDecimal } from "../../../utils/utils";
import { ProductLinkIcon } from "../../../components/common/ProductLinkIcon";
import { InfoOutlinedIcon } from "../../../components/common/iconography";

export const formValidate = (form, notify) => {
  const newFormState = form.getState();
  if (newFormState.invalid) notify(VALIDATION_MSG, "warning");
  return !newFormState.invalid;
};

export const hasWeight = (account, inventory) => {
  const product = inventory?.product;

  return (
    inventory?.weight ||
    inventory?.weightUOM ||
    product?.defaultWeightUOM ||
    account?.defaultWeightUOM
  );
};

export const showWeight = (account, inventory) => {
  return (
    hasWeight(account, inventory) &&
    inventory?.inventoryUOM !== inventory?.weightUOM
  );
};

/**
 * Default quantity UOM for a tally/inventory line when the row has no explicit UOM.
 * Prefers product defaultUOM (if present), then yard commodity units, then
 */
export const getDefaultInventoryUOM = ({
  reloadYardCommodity,
  product,
} = {}) => {
  const yardCommodity =
    typeof reloadYardCommodity === "string"
      ? getYardCommodity(reloadYardCommodity)
      : reloadYardCommodity;
  if (product?.defaultUOM) return product.defaultUOM;
  if (yardCommodity?.units) return reloadYardCommodity.units;
  return "";
};

export const handleInventoryDelete = async (inventory, dataProvider) => {
  const promiseList = [];
  debugger;

  if (inventory.photos?.items?.length > 0) {
    inventory.photos.items.forEach((itm) => {
      promiseList.push(Storage.remove(itm.key));
      promiseList.push(
        API.graphql(graphqlOperation(deletePhoto, { input: { id: itm.id } })),
      );
    });
  }

  // update location capacityUsed for STRUCTURED, not OTHER only
  inventory.inventoryItems?.items.forEach((inventoryItem) => {
    promiseList.push(promiseDeleteInventoryItem(inventoryItem));

    if (isManagedLocation(inventoryItem.location)) {
      promiseList.push(promiseResetCapacityUsed(inventory, inventoryItem));
    }
  });
  await Promise.all(promiseList);

  await dataProvider.delete("inventories", {
    id: inventory.id,
    previousData: inventory,
  });
  // await API.graphql(graphqlOperation(deleteInventorie, {input: {id: data.id}}));
};

export const resetCapacityUsed = (inventory, inventoryItem) => {
  // reset capaocity when inventoryItems are deleted.
  // update location with updated capacity and productId.
  const location = { ...inventoryItem.location };
  const isOtherLoadType = location.locationLoad?.other.selected;
  debugger;

  if (isBulkLocation(location)) {
    if (
      inventoryItem.status === InventoryStatus.IN_TRANSIT ||
      inventoryItem.status === InventoryStatus.ARRIVED
    ) {
      // if inventoryItem is not UNLOADED / STORED update capacityAllocated
      location.capacityAllocated =
        location.capacityAllocated - inventoryItem.quantityOnHand;
      // } else if (inventoryItem.status === InventoryStatus.AGGREGATED) {
      //     // if inventoryItem is AGGREGATED update capacityUsed & deaggregate
      //     location.capacityUsed = location.capacityUsed - inventoryItem.quantityOnHand;
    } else {
      // if inventoryItem is UNLOADED / STORED update capacityUsed
      location.capacityUsed =
        location.capacityUsed - inventoryItem.quantityOnHand;
    }
    // isContaminated is deliberately untouched: emptying a tank does not clean
    // it -- see canStoreInBulkTank. Only a manager clearing the tank's capacity
    // in the bulk location dialog records that it was washed out.
    // BULK productId reset done in LOCATIONS
  } else if (location.type === LocationType.STRUCTURED && !isOtherLoadType) {
    const inventoryLoadType =
      inventory.reloadYardCommodity.commodity.locationLoadType;
    const locationLoadType = location.locationLoad[inventoryLoadType];

    if (
      inventoryItem.status === InventoryStatus.IN_TRANSIT ||
      inventoryItem.status === InventoryStatus.ARRIVED
    ) {
      // if inventoryItem is not UNLOADED / STORED update capacityAllocated
      const capacityAllocated =
        location.capacityAllocated - locationLoadType.capacity;
      location.capacityAllocated = capacityAllocated;
      if (location.capacityUsed === 0 && capacityAllocated === 0)
        location.productId = null;
    } else {
      // if inventoryItem is UNLOADED / STORED update capacityUsed
      const capacityUsed = location.capacityUsed - locationLoadType.capacity;
      location.capacityUsed = capacityUsed;
      if (capacityUsed === 0 && location.capacityAllocated === 0)
        location.productId = null;
    }
  }

  return location;
};

export const promiseResetCapacityUsed = (inventory, inventoryItem) => {
  const resetLocation = resetCapacityUsed(inventory, inventoryItem);
  // resetLocation, not inventoryItem.location: the reset rebuilds the location
  // and it is the rebuilt one that has to be persisted.
  return promiseSetCapacityUsed(
    resetLocation,
    resetLocation.capacityUsed,
    resetLocation.capacityAllocated,
  );
};

const promiseDeleteInventoryItem = (inventoryItem) => {
  return API.graphql(
    graphqlOperation(deleteInventoryItem, { input: { id: inventoryItem.id } }),
  );
};

export const getInfoBubble = (inventory) => {
  const quantity = inventory?.quantityOnHand || inventory?.quantityAdvised || 1;
  const product = inventory?.product;

  // Get commodity for determining calculation type
  const commodity =
    inventory?.reloadYardCommodityId ||
    inventory?.reloadYardCommodity?.commodity;

  const infoBubble = getUnitConversions({
    inventory,
    quantity,
    fromUnits: inventory.inventoryUOM,
    commodity,
    product,
  });

  // Build tooltip content
  const hasContent = infoBubble.length > 0;

  // Show broken link icon if no product
  const showBrokenLink = !product;

  // Build tooltip title
  const tooltipTitle = (
    <>
      {product && (
        <Typography>Product Code: {inventory.productCode}</Typography>
      )}
      {infoBubble.length > 0 && (
        <>
          <Typography>
            {quantity.toLocaleString()} {getUnitName(inventory.inventoryUOM)} =
          </Typography>
          {infoBubble.map((itm) =>
            itm.value ? (
              <Typography key={itm.unit}>
                &emsp;{trimDecimal(itm.value, 3).toLocaleString()}{" "}
                {getUnitName(itm.unit)}
              </Typography>
            ) : null,
          )}
        </>
      )}
    </>
  );

  return (
    <InputAdornment position="end">
      {showBrokenLink && <ProductLinkIcon />}
      {hasContent && (
        <Tooltip title={tooltipTitle} placement="right">
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      )}
    </InputAdornment>
  );
};

export async function validateInventoryBeforeUpdate(formData, notify) {
  const staleErrors = [];
  debugger;

  // ——— 1) Always validate outbound.updatedAt ———
  let dbInventory = null;
  const result = await API.graphql(
    graphqlOperation(getInventorieValidate, { id: formData.id }),
  );
  dbInventory = result.data.getInventorie;
  if (!dbInventory) {
    staleErrors.push(`Inventory deleted.`);
    // abort further validation
    return { staleErrors };
  } else if (new Date(dbInventory.updatedAt) > new Date(formData.updatedAt)) {
    staleErrors.push(`Inventory changed.`);
  }

  return { staleErrors };
}

export const getLatestShippedOutboundId = (inventory) => {
  const shipmentLineItems = inventory?.shipmentLineItems?.items || [];

  const latestShipmentLineItem = shipmentLineItems.reduce(
    (latestLineItem, shipmentLineItem) => {
      if (
        shipmentLineItem?.status !== ShipmentLineItemStatus.SHIPPED ||
        !shipmentLineItem?.outboundShipmentId
      ) {
        return latestLineItem;
      }

      const departureDateTime =
        shipmentLineItem?.outboundShipment?.departureDateTime;
      if (!departureDateTime) {
        return latestLineItem;
      }

      const shippedAt = new Date(departureDateTime);
      if (Number.isNaN(shippedAt.getTime())) {
        return latestLineItem;
      }

      if (!latestLineItem) {
        return shipmentLineItem;
      }

      const latestShippedAt = new Date(
        latestLineItem.outboundShipment.departureDateTime,
      );

      return shippedAt > latestShippedAt ? shipmentLineItem : latestLineItem;
    },
    null,
  );

  return latestShipmentLineItem?.outboundShipmentId ?? null;
};
